'use client';

import { Box } from '@mui/material';
import { lazy, Suspense, memo, useMemo, useState, useEffect, useRef } from 'react';
import { LOTTIE_ICONS, getLottieIconPath } from './lottie-icons';

// Lazy load the Lottie player component
const LottiePlayer = lazy(() =>
    import('@lottiefiles/dotlottie-react').then((mod) => ({
        default: mod.DotLottieReact,
    }))
);

const LottieIconComponent = memo(
    ({ icon, width = '30px', height = '30px', loop = true, autoplay = true, speed = 1, ...boxProps }) => {
        const [isInView, setIsInView] = useState(false);
        const containerRef = useRef(null);

        // Obtener la ruta del archivo .lottie
        const lottiePath = useMemo(() => {
            if (!icon) {
                return null;
            }
            return getLottieIconPath(icon);
        }, [icon]);

        // Intersection Observer para lazy loading
        useEffect(() => {
            if (!containerRef.current || !lottiePath) {
                return;
            }

            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            setIsInView(true);
                            observer.disconnect();
                        }
                    });
                },
                {
                    rootMargin: '50px', // Start loading slightly before it comes into view
                    threshold: 0.01,
                }
            );

            observer.observe(containerRef.current);

            return () => {
                observer.disconnect();
            };
        }, [lottiePath]);

        // Calculate device pixel ratio for high DPI displays
        const devicePixelRatio = useMemo(() => {
            if (typeof window !== 'undefined') {
                return window.devicePixelRatio || 1;
            }
            return 1;
        }, []);

        // For high quality rendering on retina displays, render at higher resolution
        const renderMultiplier = devicePixelRatio >= 2 ? 2 : 1;

        const actualWidth = typeof width === 'number' ? width : parseInt(String(width)) || 24;
        const actualHeight = typeof height === 'number' ? height : parseInt(String(height)) || 24;
        const renderWidth = actualWidth * renderMultiplier;
        const renderHeight = actualHeight * renderMultiplier;

        if (!icon || !lottiePath) {
            return null;
        }

        return (
            <Box
                ref={containerRef}
                {...boxProps}
                sx={{
                    width,
                    height,
                    position: 'relative',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    ...boxProps.sx,
                }}
            >
                {isInView && (
                    <Suspense
                        fallback={
                            <Box
                                sx={{
                                    width,
                                    height,
                                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                    borderRadius: '4px',
                                }}
                            />
                        }
                    >
                        <Box
                            sx={{
                                width: renderWidth,
                                height: renderHeight,
                                transform: `scale(${1 / renderMultiplier})`,
                                transformOrigin: 'top left',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                            }}
                        >
                            <LottiePlayer
                                src={lottiePath}
                                loop={loop}
                                autoplay={autoplay}
                                speed={speed}
                                style={{
                                    width: renderWidth,
                                    height: renderHeight,
                                }}
                                onError={(error) => {
                                    console.error(`Error loading Lottie file: ${lottiePath}`, error);
                                }}
                            />
                        </Box>
                    </Suspense>
                )}
            </Box>
        );
    }
);

LottieIconComponent.displayName = 'LottieIconComponent';

export default LottieIconComponent;
export { LOTTIE_ICONS };
